import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, CheckCircle2, AlertCircle, RefreshCcw, ArrowLeft,
  ShieldCheck, Hourglass, XCircle,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import { getRedemption } from "@/lib/api";

const POLL_INTERVAL_MS = 5_000;
const STILL_PENDING_AFTER_MS = 10 * 60 * 1_000;

const TERMINAL = new Set(["fulfilled", "rejected", "failed_terminal"]);

const META = {
  submitted:                { tone: "info", icon: Hourglass,      label: "Submitted" },
  pending_partner_review:   { tone: "info", icon: Hourglass,      label: "Pending partner review" },
  fulfilled:                { tone: "ok",   icon: CheckCircle2,   label: "Fulfilled" },
  rejected:                 { tone: "bad",  icon: XCircle,        label: "Rejected" },
  failed_retryable:         { tone: "warn", icon: RefreshCcw,     label: "Temporarily failed (retrying)" },
  failed_terminal:          { tone: "bad",  icon: XCircle,        label: "Failed" },
};

const TONE_CLASS = {
  info: "text-[#00F0FF] border-[#00F0FF]/40",
  ok:   "text-[#39FF14] border-[#39FF14]/40 glow-green",
  warn: "text-[#FFB800] border-[#FFB800]/40",
  bad:  "text-[#FF3B30] border-[#FF3B30]/40",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

/**
 * AP-4F status polling page.
 *
 * - Polls `GET /api/redemptions/{ref}` every 5 s.
 * - Stops polling on terminal status (fulfilled / rejected / failed_terminal).
 * - After 10 min, if still pending, surfaces "you can safely leave"
 *   copy without spamming requests.
 * - Same-user only (server enforces 403); we render a friendly notice
 *   on 403/404 without leaking that the row exists.
 */
export default function RedeemStatus() {
  const { arcReferenceId } = useParams();
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const startedAt = useRef(Date.now());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Tick now() every 30 s to refresh "still pending" calculation.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let timer = null;
    let alive = true;

    const poll = async () => {
      try {
        const data = await getRedemption(arcReferenceId);
        if (!alive || !isMountedRef.current) return;
        setRow(data);
        setError(null);
        setLoading(false);
        if (TERMINAL.has(data.status)) {
          // Stop polling.
          if (timer) clearTimeout(timer);
          timer = null;
          return;
        }
      } catch (e) {
        if (!alive || !isMountedRef.current) return;
        const status = e?.response?.status;
        if (status === 401) setError({ kind: "auth" });
        else if (status === 403 || status === 404) setError({ kind: "not_found" });
        else setError({ kind: "network" });
        setLoading(false);
      }
      if (alive) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [arcReferenceId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="redeem-status-page">
      <Header />
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />
        <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="mb-6">
            <Link to="/redeem" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-[#39FF14]" data-testid="redeem-status-back">
              <ArrowLeft className="h-3 w-3" /> Back to /redeem
            </Link>
          </div>

          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          {loading && !row && !error && (
            <div data-testid="redeem-status-loading" className="glass rounded-md p-6 flex items-center gap-2 text-white/65">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-mono text-xs">loading status…</span>
            </div>
          )}

          {error && (
            <div data-testid="redeem-status-error" data-error-kind={error.kind} className="glass rounded-md p-6">
              <div className="flex items-start gap-2 text-[#FF3B30]">
                <AlertCircle className="mt-0.5 h-5 w-5" />
                <div className="font-mono text-xs">
                  <div className="font-display text-sm text-white">
                    {error.kind === "auth" && "Session expired"}
                    {error.kind === "not_found" && "We couldn't find this redemption"}
                    {error.kind === "network" && "We couldn't reach AssosPay"}
                  </div>
                  <div className="mt-1 text-white/65">
                    {error.kind === "auth" && "Please link Assos365 again to view this status."}
                    {error.kind === "not_found" && "The reference id is unknown or this redemption belongs to another account."}
                    {error.kind === "network" && "Please retry in a moment."}
                  </div>
                </div>
              </div>
            </div>
          )}

          {row && <StatusBody row={row} now={now} startedAt={startedAt.current} />}
        </main>
      </div>
      <Footer />
    </div>
  );
}

function StatusBody({ row, now, startedAt }) {
  const meta = META[row.status] || { tone: "info", icon: Hourglass, label: row.status };
  const Icon = meta.icon;
  const isTerminal = TERMINAL.has(row.status);
  const stillPending = !isTerminal && (now - startedAt > STILL_PENDING_AFTER_MS);

  return (
    <div className="space-y-4">
      <div data-testid="redeem-status-card" data-status={row.status} className="glass rounded-md p-6">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] ${TONE_CLASS[meta.tone] || TONE_CLASS.info}`}>
            <Icon className="h-4 w-4" />
            <span data-testid="redeem-status-label">{meta.label}</span>
          </span>
          {!isTerminal && (
            <span className="font-mono text-[10px] text-white/40 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> polling every 5 s
            </span>
          )}
        </div>

        <h2 className="mt-5 font-display text-sm uppercase tracking-[0.2em] text-white">
          Redemption details
        </h2>

        <dl className="mt-3 divide-y divide-white/5 font-mono text-[11px]">
          <DetailRow label="Reference">
            <span data-testid="redeem-status-ref" className="text-white/85 break-all">
              {row.arc_reference_id}
            </span>
          </DetailRow>
          <DetailRow label="ARC amount">
            <span data-testid="redeem-status-arc">{row.rate_snapshot?.arc_amount ?? "—"}</span>{" "}
            <span className="text-[#00F0FF]">ARC</span>
          </DetailRow>
          <DetailRow label="EUR amount" highlight>
            <span data-testid="redeem-status-eur" className="font-display text-lg text-[#39FF14] glow-green">
              {row.rate_snapshot?.eur_amount ?? "—"}
            </span>{" "}
            <span className="text-[#00F0FF]">EUR</span>
          </DetailRow>
          <DetailRow label="Rate (eur / arc)">
            {row.rate_snapshot?.eur_per_arc ?? "—"}
          </DetailRow>
          <DetailRow label="Rate id">
            <span className="text-white/55 break-all">{row.rate_snapshot?.rate_id ?? "—"}</span>
          </DetailRow>
          <DetailRow label="Quoted at">{fmt(row.rate_snapshot?.quoted_at)}</DetailRow>
          <DetailRow label="Created">{fmt(row.created_at)}</DetailRow>
          <DetailRow label="Updated">{fmt(row.updated_at)}</DetailRow>
          <DetailRow label="Sent">{fmt(row.sent_at)}</DetailRow>
          {row.completed_at && <DetailRow label="Completed">{fmt(row.completed_at)}</DetailRow>}
          {row.rejected_at && <DetailRow label="Rejected">{fmt(row.rejected_at)}</DetailRow>}
          {row.failed_at && <DetailRow label="Failed">{fmt(row.failed_at)}</DetailRow>}
          {row.assos365_transaction_id && (
            <DetailRow label="Assos365 transaction id">
              <span data-testid="redeem-status-tx" className="text-white/85 break-all">
                {row.assos365_transaction_id}
              </span>
            </DetailRow>
          )}
          {row.partner_status && (
            <DetailRow label="Partner status">{row.partner_status}</DetailRow>
          )}
          {row.last_error && (
            <DetailRow label="Last error">
              <span data-testid="redeem-status-last-error" className="text-[#FF3B30]">{row.last_error}</span>
            </DetailRow>
          )}
        </dl>
      </div>

      {!isTerminal && stillPending && (
        <div data-testid="redeem-status-still-pending" className="glass rounded-md p-5 border border-[#00F0FF]/30">
          <p className="font-mono text-[11px] text-white/70">
            Still pending. You can safely leave this page and check again later — your redemption will continue processing.
          </p>
        </div>
      )}

      {row.status === "fulfilled" && (
        <div className="glass rounded-md p-5 border border-[#39FF14]/30">
          <div className="flex items-start gap-2 text-[#39FF14]">
            <ShieldCheck className="mt-0.5 h-4 w-4" />
            <p className="font-mono text-[11px] text-white/80">
              Assos365 has credited your account. The locked rate
              snapshot above is what was applied.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, children, highlight }) {
  return (
    <div className="flex items-baseline justify-between py-2 gap-3">
      <dt className="text-[10px] uppercase tracking-[0.2em] text-white/40 shrink-0">{label}</dt>
      <dd className={`text-right ${highlight ? "" : "text-white/80"}`}>{children}</dd>
    </div>
  );
}
